<script lang="ts">
	//Design system components
	import { beforeUpdate, afterUpdate, onDestroy, createEventDispatcher, onMount } from 'svelte';
	import {
		toast,
		IconButton,
		Modal,
		CircleAvatar,
		ImageUpload,
		ImageGroup,
		Alert,
		Container,
		Text,
		ButtonV2,
	} from '@Shelly/shelly-design-system';
	import type {
		ImageUploadPropsTypes,
		AlertPropsTypes,
		ImageGroupImageType,
	} from '@Shelly/shelly-design-system';
	import {
		getImages,
		mapImgArray,
		uploadImage,
		// saveCustomIconForDevice,
		// loadCustomIconsForDevice,
		// getImagesByType,
	} from './list-helpers';
	import { format } from 'svelte-i18n';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { postReq } from 'src/services/http';
	import { user_data } from 'src/stores/account/user';
	import { capFirstChar } from 'src/helpers/utilities';
	import IconSearchModal from './IconSearchModal.svelte';
	import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
	import { load_image_from } from 'src/helpers/interface_helpers';

	const dispatch = createEventDispatcher();

	export let requestType = 'room';
	export let selectedImage = '';
	export let selectedBackgroundColor = '';
	export let selectedIcon = '';
	export let showColors = true;
	/** When false, hides the custom-picture upload ImageGroup. */
	export let showUpload = true;
	/** When false, hides the default/type pictures ImageGroup. */
	export let showTypeImages = true;
	/** When false, hides the "Other" pictures ImageGroup. */
	export let showOtherImages = true;
	/** When false, hides the icons ImageGroup (even if iconsDB is non-empty). */
	export let showIcons = true;
	/**
	 * When true, selecting a color keeps the current icon (and vice versa).
	 * Used by the dashboard editor where icon + backgroundColor coexist.
	 */
	export let coexistIconAndBackgroundColor = false;
	/**
	 * When true and `backgroundColorPreviewImages` is set, only tokens present
	 * in that map are shown (solid/gradient home-background swatches).
	 */
	export let restrictColorsToPreviewImages = false;
	export let uploadedImagesDB = [];
	export let imagesByTypeDB = [];
	export let setDefaultImage = false;
	export let groupTitle: {
		upload?: string;
		type?: string;
		other?: string;
	} = {
		upload: $format('title_uploaded_pictures', {
			default: 'Uploaded pictures',
		}),
		type: $format('title_default_pictures', { default: 'Default pictures' }),
	};
	export let iconsDB = [];

	export let uploadSettings: {
		single: boolean;
		imageUploadSettings?: {
			type: ImageUploadPropsTypes['type'];
			btnSettings: ImageUploadPropsTypes['btnSettings'];
			useV2Components?: boolean;
		};
	} = {
		single: false,
		imageUploadSettings: {
			type: 'circle',
			btnSettings: null,
			useV2Components: false,
		},
	};

	export let updateTypeImages = false;
	export let uploadImageAlert: {
		heading: AlertPropsTypes['heading'];
		type: AlertPropsTypes['type'];
	} = null;

	export let defaultType: ImageUploadPropsTypes['type'] = 'circle';
	export let imgGroupType: ImageUploadPropsTypes['type'] = 'circle';
	export let titleStyleType: 'body-small' | 'label-small' = 'label-small';

	// Prop to receive persisted custom icons
	export let customIcons = [];

	// Optional map of backgroundColor token -> preview image path (solid colors and gradients
	// alike) so a color swatch renders a pixel-accurate preview instead of the CSS color/gradient
	// class. The 35% dark overlay is drawn on top via CSS (see `.color-swatch-overlay` below),
	// not baked into the image, so it always matches `--home-background-overlay-opacity` (src/app.css).
	// See src/helpers/background_color_helpers.ts.
	export let backgroundColorPreviewImages: Record<string, string> = {};

	const imageGroupTypes = {
		imgType: 'imgType',
		uploaded: 'uploaded',
		other: 'other',
		color: 'color',
		icon: 'icon',
	};

	let uploadedImages = [];
	let imagesByType = [];
	let otherImages = [];
	let imgsSet = false;
	let defaultImageSet = false;
	let showModal = false;
	let fileInputRef: HTMLInputElement;
	let showIconFolderModal = false;

	const colorValues = Object.values(AvatarColorVariant).slice(0, -5);

	const buildColorSwatches = (): ImageGroupImageType[] => {
		const tokens = restrictColorsToPreviewImages
			? Object.keys(backgroundColorPreviewImages)
			: (colorValues as string[]);

		return tokens.map((token, i) => {
			const currentColor = token as ImageGroupImageType['backgroundColor'];
			const previewImage = backgroundColorPreviewImages[currentColor];
			return {
				backgroundColor: currentColor,
				...(previewImage ? { image: load_image_from(previewImage) } : {}),
				id: i,
				selected: selectedBackgroundColor === currentColor,
				type: 'color',
			};
		});
	};

	let imgGroupColors: ImageGroupImageType[] = buildColorSwatches();

	onMount(() => {
		setBackgroundColors();
		// Initialize with custom icons if provided
		if (customIcons.length > 0) {
			mergeCustomIconsWithDefaults();
		}
	});

	onDestroy(() => {
		selectedImage = '';
		selectedBackgroundColor = '';
		selectedIcon = '';
	});

	$: if (imagesByTypeDB) {
		setDefaultRoomImage();
	}

	beforeUpdate(() => {
		if (!imgsSet) {
			setDefaultRoomImage();
			setImagesForImgGroups();
			imgsSet = true;
		}
	});

	afterUpdate(() => {
		if (setDefaultImage && defaultImageSet) {
			dispatchSelectImage();
			defaultImageSet = false;
		}
	});

	const dispatchSelectImage = () => {
		dispatch('select-image', {
			image: selectedImage,
			backgroundColor: selectedBackgroundColor,
			icon: selectedIcon,
			isIcon: !!selectedIcon,
		});
	};

	const setDefaultRoomImage = () => {
		if (setDefaultImage && !defaultImageSet) {
			selectedImage = imagesByTypeDB[0]?.filepath;
			defaultImageSet = true;
		}
	};

	const getAllImagesFromGroups = () => {
		return [...uploadedImages, ...imagesByType, ...otherImages, ...imgGroupColors, ...iconsDB];
	};

	const setUploadedImages = () => {
		uploadedImages = mapImgArray(uploadedImagesDB, imageGroupTypes.uploaded, selectedImage);
	};

	// Merge custom icons with default images
	const mergeCustomIconsWithDefaults = () => {
		const formattedCustomIcons = customIcons.map((iconClass, index) => ({
			id: `custom-icon-${index}`,
			icon: iconClass,
			isIcon: true,
			selected: selectedIcon === iconClass,
			type: imageGroupTypes.imgType,
			local: false,
		}));

		// add the custom icons
		imagesByType = [...imagesByType, ...formattedCustomIcons];
	};

	const setImagesByType = () => {
		// Process all images including icons
		imagesByType = mapImgArray(
			imagesByTypeDB,
			imageGroupTypes.imgType,
			selectedImage,
			selectedIcon,
		).sort((a, b) => a.isIcon - b.isIcon);

		if (customIcons.length > 0) {
			mergeCustomIconsWithDefaults();
		}
	};

	const setImagesForImgGroups = () => {
		setImagesByType();
		setUploadedImages();
	};

	const setBackgroundColors = () => {
		let selectedIndex = -1;

		let mappedArray = imgGroupColors.map((color, index) => {
			const isSelected = color.backgroundColor === selectedBackgroundColor;

			if (isSelected) selectedIndex = index;

			return {
				...color,
				selected: isSelected,
			};
		});

		const selectedColor = mappedArray[selectedIndex];
		if (selectedColor) {
			mappedArray.splice(selectedIndex, 1);
			imgGroupColors = [selectedColor, ...mappedArray];
		}
	};

	const filterAllImagesByGroup = (allImages) => {
		uploadedImages = allImages.filter((img) => img.type === imageGroupTypes.uploaded);
		imagesByType = allImages.filter((img) => img.type === imageGroupTypes.imgType);
		otherImages = allImages.filter((img) => img.type === imageGroupTypes.other);
		imgGroupColors = allImages.filter((img) => img.type === imageGroupTypes.color);
		// Reassign so icon selection state stays reactive (iconsDB is otherwise a
		// parent-owned array that wouldn't update when we map selected flags).
		const nextIcons = allImages.filter((img) => img.type === imageGroupTypes.icon);
		if (nextIcons.length) {
			iconsDB = nextIcons;
		}
	};

	const closeModal = () => {
		showModal = false;
	};

	const showDeleteModal = () => {
		showModal = true;
	};

	const openFilePicker = () => {
		fileInputRef?.click();
	};

	const removeActiveImage = () => {
		const allImages = getAllImagesFromGroups().map((img) => {
			return {
				...img,
				selected: false,
			};
		});

		filterAllImagesByGroup(allImages);
	};

	const markSelectedIcon = (iconClass: string) => {
		const allImages = getAllImagesFromGroups();
		const updatedImages = allImages.map((img) => ({
			...img,
			selected: img.icon === iconClass && img.isIcon,
		}));
		filterAllImagesByGroup(updatedImages);
	};

	const openIconFolderModal = () => {
		showIconFolderModal = true;
	};

	const closeIconFolderModal = () => {
		showIconFolderModal = false;
	};

	// Handle icon selection from modal
	const onIconSelected = (event) => {
		const iconClass = event.detail.icon;

		// Clear all current selections
		removeActiveImage();

		// Set the new selections
		selectedIcon = iconClass;
		selectedImage = '';
		selectedBackgroundColor = '';

		// Check if icon already exists in the list
		const existingIcon = imagesByType.find((img) => img.icon === iconClass && img.isIcon);

		if (existingIcon) {
			// Icon already exists, just select it
			markSelectedIcon(iconClass);
		} else {
			// A new custom icon - so we add it temporarily to the list
			const newIcon = {
				id: `custom-icon-new`,
				icon: iconClass,
				isIcon: true,
				selected: true,
				type: imageGroupTypes.imgType,
				local: false,
				isCustom: true,
			};

			imagesByType = [...imagesByType, newIcon];
		}

		// update the selection state across all groups
		const allImages = getAllImagesFromGroups();
		const updatedImages = allImages.map((img) => ({
			...img,
			selected: img.icon === iconClass && img.isIcon,
		}));

		// Update all image groups with the new selection
		filterAllImagesByGroup(updatedImages);

		// Dispatch the selection to parent
		dispatchSelectImage();

		// Dispatch custom icon event for parent to handle
		dispatch('custom-icon-selected', {
			icon: iconClass,
		});
	};

	const isImgSelected = (img: ImageGroupImageType) => {
		if (
			coexistIconAndBackgroundColor &&
			((selectedBackgroundColor && img.backgroundColor === selectedBackgroundColor) ||
				(selectedIcon && img.icon === selectedIcon && img.isIcon))
		) {
			return true;
		}

		if (selectedBackgroundColor && img.backgroundColor === selectedBackgroundColor) {
			return true;
		}
		if (selectedIcon && img.icon === selectedIcon && img.isIcon) {
			return true;
		}
		if (selectedImage && (img.image === selectedImage || img.filepath === selectedImage)) {
			return true;
		}
		return false;
	};

	// Handle image/icon/color selection from ImageGroups
	const onSelectImg = (event) => {
		const { detail } = event;

		// Clear all selections first (coexist mode keeps the other of icon/color)
		removeActiveImage();

		if (detail.backgroundColor) {
			selectedBackgroundColor = detail.backgroundColor;
			selectedImage = '';
			if (!coexistIconAndBackgroundColor) {
				selectedIcon = '';
			}
		} else if (detail.icon) {
			selectedIcon = detail.icon;
			selectedImage = '';
			if (!coexistIconAndBackgroundColor) {
				selectedBackgroundColor = '';
			}
		} else if (detail.image || detail.filepath) {
			selectedImage = detail.filepath;
			selectedBackgroundColor = '';
			selectedIcon = '';
		}

		const updatedImages = getAllImagesFromGroups().map((img) => ({
			...img,
			selected: isImgSelected(img),
		}));

		filterAllImagesByGroup(updatedImages);

		dispatchSelectImage();
	};

	const onUploadImage = async (event) => {
		const result = await uploadImage(event, requestType);
		if (Array.isArray(result)) {
			uploadedImagesDB = result;
			toast.open({
				type: 'success',
				title: $format('toast_title_picture_uploaded', {
					default: 'Picture uploaded successfully.',
				}),
			});

			removeActiveImage();

			selectedImage = uploadedImagesDB[0].filepath;
			uploadedImages = mapImgArray(uploadedImagesDB, imageGroupTypes.uploaded, selectedImage);

			// to be able to upload same img after that
			event.target.value = '';

			dispatch('upload-image', {
				image: selectedImage,
				uploadedImagesDB: uploadedImagesDB,
			});
		}
	};

	const onDeleteImage = async () => {
		const imgToDelete = uploadedImages[0];

		const res = await postReq('/user_files/delete_image', {
			img_path: imgToDelete.filepath.replace('assets/user_images/', '').replace('thumb_', ''),
		});

		if (res.isok) {
			toast.open({
				type: 'success',
				title: $format('toast_title_picture_deleted', {
					default: 'Picture deleted successfully.',
				}),
			});

			uploadedImages = uploadedImages.filter((img) => img.filepath !== imgToDelete.filepath);
			uploadedImagesDB = await getImages(requestType);

			if (selectedImage === imgToDelete.filepath) {
				selectedImage = imagesByType[0].filepath;
				const imagesFromGroup = getAllImagesFromGroups();
				const selectedImg0bj = imagesFromGroup.find((img) => img.filepath === selectedImage);
				const allImages = imagesFromGroup.map((img) => {
					if (selectedImg0bj.id === img.id && img.type === selectedImg0bj.type) {
						selectedImage = img.filepath;
						selectedBackgroundColor = '';

						return {
							...img,
							selected: true,
						};
					}

					return {
						...img,
						selected: false,
					};
				});

				filterAllImagesByGroup(allImages);
			}

			dispatch('delete-image', {
				image: selectedImage,
				uploadedImagesDB: uploadedImagesDB,
			});
		}
	};

	const deleteBtnHandler = () => {
		closeModal();
		onDeleteImage();
	};

	$: (customIcons, mergeCustomIconsWithDefaults());
	$: (uploadedImagesDB, setUploadedImages());
	$: (imagesByTypeDB || updateTypeImages, setImagesByType());
	$: isAlert = uploadImageAlert && uploadImageAlert.heading;
	// Only the home background picker passes preview images for its color/gradient swatches -
	// scope the overlay CSS (see the style block below) to just that usage so other color
	// pickers (rooms, devices, groups, etc.) that render plain CSS color swatches are unaffected.
	$: hasBackgroundColorPreviewImages = Object.keys(backgroundColorPreviewImages).length > 0;
	$: uploadBtnSettings = {
		title: $format('button_title_picture_upload', {
			default: 'Upload picture',
		}),
		variant: 'secondary',
		icon: 'fa-solid fa-upload',
		editTypePrimaryBtnIcon: 'fa-solid fa-arrows-rotate',
		editTypePrimaryBtnTitle: $format('button_title_picture_change', {
			default: 'Change picture',
		}),
		deleteTypePrimaryBtnIcon: 'fa-solid fa-trash',
		deleteTypePrimaryBtnTitle: $format('button_title_picture_delete', {
			default: 'Delete picture',
		}),
		...(uploadSettings?.imageUploadSettings?.btnSettings || {}),
	} as ImageUploadPropsTypes['btnSettings'];

	// Keep selection highlights in sync when parent-controlled props change
	$: if (selectedIcon || selectedBackgroundColor || selectedImage) {
		const updatedImages = getAllImagesFromGroups().map((img) => ({
			...img,
			selected: isImgSelected(img),
		}));
		filterAllImagesByGroup(updatedImages);
	}
</script>

{#if showUpload}
	<ImageGroup
		class={classMap({ 'mb-l': !isAlert, 'mb-s': isAlert })}
		title={groupTitle.upload}
		{titleStyleType}
		images={uploadedImages}
		single={uploadSettings.single}
		on:click={onSelectImg}
	>
		<div slot="imgUpload">
			{#if uploadSettings?.imageUploadSettings?.useV2Components}
				<div class="d-flex flex-direction-column gap-s">
					<ButtonV2
						size="m"
						variant="primary"
						icon={uploadedImages.length > 0
							? uploadBtnSettings.editTypePrimaryBtnIcon
							: uploadBtnSettings.icon}
						aria-label={uploadedImages.length > 0
							? uploadBtnSettings.editTypePrimaryBtnTitle
							: uploadBtnSettings.title}
						on:click={openFilePicker}
					>
						{uploadedImages.length > 0
							? uploadBtnSettings.editTypePrimaryBtnTitle
							: uploadBtnSettings.title}
					</ButtonV2>
					{#if uploadedImages.length > 0}
						<ButtonV2
							size="m"
							variant="danger"
							icon={uploadBtnSettings.deleteTypePrimaryBtnIcon}
							aria-label={uploadBtnSettings.deleteTypePrimaryBtnTitle}
							on:click={showDeleteModal}
						>
							{uploadBtnSettings.deleteTypePrimaryBtnTitle}
						</ButtonV2>
					{/if}
				</div>
				<input
					bind:this={fileInputRef}
					id="input-image-list-upload-{requestType}"
					hidden
					type="file"
					accept=".jpg, .jpeg, .heif, .png"
					aria-label={uploadBtnSettings.title}
					on:change={onUploadImage}
				/>
			{:else}
				<ImageUpload
					accept=".jpg, .jpeg, .heif, .png"
					on:change={onUploadImage}
					on:delete={showDeleteModal}
					{...uploadSettings.imageUploadSettings}
					type={uploadedImages.length > 0 ? defaultType : imgGroupType}
					btnSettings={uploadBtnSettings}
				/>
			{/if}
		</div>
	</ImageGroup>
{/if}

{#if isAlert}
	<Alert class="mt-s mb-l" heading={uploadImageAlert.heading} type={uploadImageAlert.type} />
{/if}

{#if showIcons && iconsDB?.length}
	<ImageGroup
		class="mb-l text-capitalize"
		title={$format('icons', {
			default: 'icons',
		})}
		images={iconsDB}
		on:click={onSelectImg}
	/>
{/if}

{#if showTypeImages}
	<ImageGroup
		class={classMap({
			'image-type-title': true,
			'text-capitalize': titleStyleType !== 'body-small',
		})}
		title={groupTitle.type}
		{titleStyleType}
		images={imagesByType}
		on:click={onSelectImg}
	>
		<div slot="imgUpload" role="button">
			{#if requestType === 'device'}
				<IconButton
					aria-label={$format('aria_search_icons', { default: 'Search Icons' })}
					variant="secondary"
					icon="fa-regular fa-plus"
					style="border-radius: 50%; padding: 32px 22px;"
					on:click={openIconFolderModal}
					on:keydown={(e) => e.key === 'Enter' && openIconFolderModal()}
					role="button"
					tabindex="0"
					title={$format('button_title_search_icons', {
						default: 'Search Icons',
					})}
				></IconButton>
			{/if}
		</div>
	</ImageGroup>
{/if}

{#if showOtherImages && otherImages?.length}
	<ImageGroup
		class="mt-l"
		title={$format('appliance_type_other', {
			default: 'Other',
		})}
		images={otherImages}
		on:click={onSelectImg}
	/>
{/if}

{#if showColors}
	<ImageGroup
		class={classMap({
			'mt-l': true,
			'background-color-swatch-group': hasBackgroundColorPreviewImages,
		})}
		title={$format('image_group_title_colors_gradients', {
			default: 'Colors and gradients.',
		})}
		{titleStyleType}
		images={imgGroupColors}
		on:click={onSelectImg}
	/>
{/if}

<Modal
	{showModal}
	title={`${capFirstChar(
		$format('button_delete', { default: 'Delete' }),
	)} ${capFirstChar($format('process_image', { default: 'Image' }))}?`}
	on:close={closeModal}
	class="justify-content-center"
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<div class="d-flex justify-content-center mb-m">
				<CircleAvatar
					image={$user_data.user_api_url + '/shelly_files/' + uploadedImages[0].filepath}
					size="xl"
				/>
			</div>
			<Text>
				{$format('delete_img_msg', {
					default:
						"Deleting this image will permanently remove it from your account. Once deleted, you won't be able to find it in your uploaded pictures.",
				})}
			</Text>
		</Container>
	</svelte:fragment>
	<svelte:fragment slot="footer">
		<div class="d-flex justify-content-end">
			<ButtonV2
				size="m"
				variant="secondary"
				on:click={closeModal}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				aria-label={$format('button_cancel', { default: 'Cancel' })}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<ButtonV2
				size="m"
				aria-label={$format('aria_delete_image', { default: 'Delete image' })}
				on:click={deleteBtnHandler}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
			>
				{capFirstChar($format('button_delete', { default: 'Delete' }))}
				{$format('process_image', { default: 'Image' })}
			</ButtonV2>
		</div>
	</svelte:fragment>
</Modal>

<IconSearchModal
	showModal={showIconFolderModal}
	on:close={closeIconFolderModal}
	on:icon-selected={onIconSelected}
/>

<style>
	/*
	 * Draws the same dark overlay used on the live rendered home background (see
	 * `.home-background-overlay` in src/routes/MainLayout.svelte / MainLayoutNew.svelte) on top
	 * of each color/gradient swatch image, so the picker preview always matches the rendered
	 * background pixel-for-pixel without baking the overlay into the image files themselves.
	 * Both read the same single `--home-background-overlay-opacity` variable (src/app.css).
	 * Assumes every swatch in this group has an `image` (see background_color_helpers.ts) -
	 * add an image for any newly-added color/gradient token to keep this accurate.
	 */
	:global(.background-color-swatch-group .img-content)::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 50%;
		background: rgba(0, 0, 0, var(--home-background-overlay-opacity));
		pointer-events: none;
	}
</style>
